/** The catalog as the editor looks fields up: by entity and key — a record kind's catalog may
 *  hold one key on two entities (the itinerary's `description` and its days' `description`,
 *  `itinerary:<id>` and `day:<id>`, O10) — and by key alone where only one entity holds it.
 *
 *  - `fieldOf(index, entity, key)` — the entity's own field of that key; else the one field
 *    of that key whatever its entity (a field the record kind shows under another record,
 *    as before); a key two entities share is never answered for a third.
 *  - `byKey` — the keys one entity holds. A shared key is left out, so a lookup by key alone
 *    finds nothing rather than the other entity's field.
 *
 *  A catalog whose keys are all unique (every record kind but the itinerary) is looked up
 *  exactly as by key alone. Pure. */
import type { RecordField } from './types';

export interface FieldIndex {
  /** By key, for the keys one entity holds. */
  byKey: ReadonlyMap<string, RecordField>;
  /** By entity, then key. */
  byEntity: ReadonlyMap<string, ReadonlyMap<string, RecordField>>;
}

export function fieldIndex(fields: readonly RecordField[]): FieldIndex {
  const byEntity = new Map<string, Map<string, RecordField>>();
  const entitiesOf = new Map<string, Set<string>>();
  const byKey = new Map<string, RecordField>();
  for (const f of fields) {
    let own = byEntity.get(f.entity);
    if (!own) byEntity.set(f.entity, (own = new Map()));
    own.set(f.key, f);
    let entities = entitiesOf.get(f.key);
    if (!entities) entitiesOf.set(f.key, (entities = new Set()));
    entities.add(f.entity);
    byKey.set(f.key, f);
  }
  for (const [key, entities] of entitiesOf) if (entities.size > 1) byKey.delete(key);
  return { byKey, byEntity };
}

/** The field `key` of `entity` (see above); undefined when the catalog has none for it. */
export function fieldOf(index: FieldIndex, entity: string, key: string): RecordField | undefined {
  return index.byEntity.get(entity)?.get(key) ?? index.byKey.get(key);
}
